import { _ as _define_property } from "@swc/helpers/_/_define_property";
import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { Decimal } from 'decimal.js';
import { compact } from 'lodash-es';
import { currencyUtils } from '~/utils/currency.utils';
import { BillingTimelineHorizon } from './BillingTimelineHorizon';
import { fromDateStampObject, fromDatestampString, toDateStampObject } from './time';
import { calendarMonthOf, dayOrderOf, monthHeadingOf, monthKeyOf, monthOrderOf, monthTickLabelOf } from './time/month';
export const MALFORMED_BILLING_TIMELINE = 'Malformed billing timeline response';
const CALENDAR_DATE = /^\d{4}-\d{2}-\d{2}$/;
const KNOWN_BILLING_TYPES = [
    'recurring',
    'oneTime'
];
const rejectMalformedResponse = (expected)=>{
    throw new Error("".concat(MALFORMED_BILLING_TIMELINE, ": expected ").concat(expected));
};
const toCalendarDate = (value)=>CALENDAR_DATE.test(value) ? toDateStampObject(fromDatestampString(value)) : rejectMalformedResponse('a date');
const toAmountInCents = (value)=>Number.isFinite(value) ? value : rejectMalformedResponse('an amount');
const toText = (value)=>typeof value === 'string' ? value : rejectMalformedResponse('text');
const toFlag = (value)=>{
    if (value == null) {
        return false;
    }
    return typeof value === 'boolean' ? value : rejectMalformedResponse('a flag');
};
const toList = (value)=>{
    if (value == null) {
        return [];
    }
    return Array.isArray(value) && value.every((element)=>element != null && typeof element === 'object') ? value : rejectMalformedResponse('a list');
};
const toBillingType = (value)=>{
    var _KNOWN_BILLING_TYPES_find;
    return (_KNOWN_BILLING_TYPES_find = KNOWN_BILLING_TYPES.find((known)=>known === value)) !== null && _KNOWN_BILLING_TYPES_find !== void 0 ? _KNOWN_BILLING_TYPES_find : 'other';
};
const toWholeNumber = (value)=>Number.isInteger(value) ? value : rejectMalformedResponse('a stage number');
const toChargeStage = (stageNumber, stageCount)=>stageNumber == null || stageCount == null ? null : {
        number: toWholeNumber(stageNumber),
        count: toWholeNumber(stageCount)
    };
const UNTAXED = {
    kind: 'untaxed'
};
const toTaxedAmount = (value)=>value != null && [
        value.amountExcludingTax,
        value.taxAmount,
        value.amountIncludingTax
    ].every((amount)=>Number.isFinite(amount)) ? value : rejectMalformedResponse('a taxed amount');
const toLineTax = (taxStatus, taxedAmount)=>taxStatus === 'taxedAmount' ? {
        kind: 'taxed',
        taxedAmount: toTaxedAmount(taxedAmount)
    } : UNTAXED;
const toInvoiceTax = (appliedTax, totals)=>{
    if (appliedTax == null) {
        return UNTAXED;
    }
    const { subtotal, tax } = totals !== null && totals !== void 0 ? totals : rejectMalformedResponse('invoice totals');
    return {
        kind: 'taxed',
        label: toText(appliedTax.label),
        rate: toText(appliedTax.totalRate),
        subtotal: toAmountInCents(subtotal),
        tax: toAmountInCents(tax)
    };
};
const toTotals = (value)=>({
        subtotal: toAmountInCents(value.subtotal),
        tax: toAmountInCents(value.tax),
        withoutCredits: toAmountInCents(value.withoutCredits),
        credits: toAmountInCents(value.credits)
    });
const toOptionalLinesTotals = (value)=>value == null ? null : toTotals(value);
const toMonthTotals = (months)=>months == null ? null : new Map(Object.entries(months).map((param)=>{
        let [key, month] = param;
        return [
            key,
            {
                totalAmount: toAmountInCents(month.totalAmount),
                totals: toTotals(month.totals)
            }
        ];
    }));
const sumAmounts = (lineItems)=>lineItems.reduce((total, lineItem)=>total + lineItem.amount(), 0);
const committedAmong = (lineItems)=>lineItems.filter((lineItem)=>!lineItem.isOptional());
const optionalAmong = (lineItems)=>lineItems.filter((lineItem)=>lineItem.isOptional());
export class ScheduledLineItemModel {
    static fromDTO(dto) {
        return new ScheduledLineItemModel(toText(dto.serviceId), toText(dto.name), toAmountInCents(dto.amount), toBillingType(dto.billingType), toFlag(dto.isFinalCharge), toFlag(dto.isProrated), toFlag(dto.isOptional), toFlag(dto.isCatchUp), toChargeStage(dto.stageNumber, dto.stageCount), {
            tax: toLineTax(dto.taxStatus, dto.taxedAmount)
        });
    }
    stage() {
        return this.billedStage;
    }
    serviceId() {
        return this.id;
    }
    name() {
        return this.serviceName;
    }
    amount() {
        return this.amountInCents;
    }
    billingType() {
        return this.type;
    }
    formattedAmount() {
        return currencyUtils.formatCents(this.amountInCents);
    }
    isFinalCharge() {
        return this.endsTheService;
    }
    isProrated() {
        return this.coversAPartPeriod;
    }
    isOptional() {
        return this.awaitsAnOptIn;
    }
    isCatchUp() {
        return this.catchesUpElapsedPeriods;
    }
    tax() {
        return this.taxFields.tax;
    }
    formattedTaxAmount() {
        const tax = this.taxFields.tax;
        return tax.kind === 'taxed' ? currencyUtils.formatCents(tax.taxedAmount.taxAmount) : null;
    }
    formattedAmountIncludingTax() {
        const tax = this.taxFields.tax;
        return tax.kind === 'taxed' ? currencyUtils.formatCents(tax.taxedAmount.amountIncludingTax) : null;
    }
    constructor(id, serviceName, amountInCents, type, endsTheService, coversAPartPeriod, awaitsAnOptIn, catchesUpElapsedPeriods, billedStage, taxFields){
        _define_property(this, "id", void 0);
        _define_property(this, "serviceName", void 0);
        _define_property(this, "amountInCents", void 0);
        _define_property(this, "type", void 0);
        _define_property(this, "endsTheService", void 0);
        _define_property(this, "coversAPartPeriod", void 0);
        _define_property(this, "awaitsAnOptIn", void 0);
        _define_property(this, "catchesUpElapsedPeriods", void 0);
        _define_property(this, "billedStage", void 0);
        _define_property(this, "taxFields", void 0);
        this.id = id;
        this.serviceName = serviceName;
        this.amountInCents = amountInCents;
        this.type = type;
        this.endsTheService = endsTheService;
        this.coversAPartPeriod = coversAPartPeriod;
        this.awaitsAnOptIn = awaitsAnOptIn;
        this.catchesUpElapsedPeriods = catchesUpElapsedPeriods;
        this.billedStage = billedStage;
        this.taxFields = taxFields;
    }
}
export class ScheduledInvoiceModel {
    static fromDTO(dto) {
        return new ScheduledInvoiceModel(toCalendarDate(dto.date), toAmountInCents(dto.totalAmount), toFlag(dto.requiresApproval), toFlag(dto.isCatchUp), toList(dto.lineItems).map(ScheduledLineItemModel.fromDTO), {
            tax: toInvoiceTax(dto.appliedTax, dto.totals),
            optionalLinesTotals: toOptionalLinesTotals(dto.optionalLinesTotals)
        });
    }
    date() {
        return this.invoiceDate;
    }
    key() {
        return "".concat(this.invoiceDate.year, "-").concat(this.invoiceDate.month, "-").concat(this.invoiceDate.day);
    }
    formattedDate() {
        return fromDateStampObject(this.invoiceDate).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric'
        });
    }
    formattedDateWithYear() {
        return fromDateStampObject(this.invoiceDate).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    }
    totalAmount() {
        return this.totalInCents;
    }
    formattedTotal() {
        return currencyUtils.formatCents(this.totalInCents);
    }
    requiresApproval() {
        return this.needsApproval;
    }
    isCatchUp() {
        return this.issuedOnSigningForAPastDate;
    }
    lineItems() {
        return this.items;
    }
    optionalAmount() {
        return sumAmounts(optionalAmong(this.items));
    }
    formattedOptionalAmount() {
        return currencyUtils.formatCents(this.optionalAmount());
    }
    hasTaxedOptionalCharges() {
        return optionalAmong(this.items).some((lineItem)=>lineItem.tax().kind === 'taxed');
    }
    hasOptionalCharges() {
        return this.optionalAmount() !== 0;
    }
    carriesCommittedCharges() {
        return committedAmong(this.items).length > 0;
    }
    tax() {
        return this.taxFields.tax;
    }
    formattedTaxSummary() {
        const tax = this.taxFields.tax;
        return tax.kind === 'taxed' ? {
            label: tax.label,
            rate: tax.rate,
            subtotal: currencyUtils.formatCents(tax.subtotal),
            tax: currencyUtils.formatCents(tax.tax)
        } : null;
    }
    formattedOptionalTotals() {
        const optionalLinesTotals = this.taxFields.optionalLinesTotals;
        return optionalLinesTotals == null ? null : {
            excludingTax: currencyUtils.formatCents(optionalLinesTotals.subtotal),
            includingTax: currencyUtils.formatCents(optionalLinesTotals.withoutCredits)
        };
    }
    constructor(invoiceDate, totalInCents, needsApproval, issuedOnSigningForAPastDate, items, taxFields){
        _define_property(this, "invoiceDate", void 0);
        _define_property(this, "totalInCents", void 0);
        _define_property(this, "needsApproval", void 0);
        _define_property(this, "issuedOnSigningForAPastDate", void 0);
        _define_property(this, "items", void 0);
        _define_property(this, "taxFields", void 0);
        this.invoiceDate = invoiceDate;
        this.totalInCents = totalInCents;
        this.needsApproval = needsApproval;
        this.issuedOnSigningForAPastDate = issuedOnSigningForAPastDate;
        this.items = items;
        this.taxFields = taxFields;
    }
}
export class MonthGroupModel {
    key() {
        return monthKeyOf(this.month);
    }
    chronologicalOrder() {
        return monthOrderOf(this.month);
    }
    heading() {
        return monthHeadingOf(this.month);
    }
    items() {
        return this.monthItems;
    }
    constructor(month, monthItems){
        _define_property(this, "month", void 0);
        _define_property(this, "monthItems", void 0);
        this.month = month;
        this.monthItems = monthItems;
    }
}
export class InvoiceMonthGroupModel extends MonthGroupModel {
    invoices() {
        return this.items();
    }
}
const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;
const dayCountBetween = (from, to)=>Math.round((fromDateStampObject(to).getTime() - fromDateStampObject(from).getTime()) / MILLISECONDS_PER_DAY);
const earlierOf = (left, right)=>dayCountBetween(left, right) < 0 ? right : left;
const windowStartOf = (anchor, invoices)=>invoices.map((invoice)=>invoice.date()).reduce(earlierOf, anchor);
const lineItemsByServiceIdOf = (invoice)=>invoice.lineItems().reduce((lineItemsByServiceId, lineItem)=>{
        var _lineItemsByServiceId_get;
        return lineItemsByServiceId.set(lineItem.serviceId(), [
            ...(_lineItemsByServiceId_get = lineItemsByServiceId.get(lineItem.serviceId())) !== null && _lineItemsByServiceId_get !== void 0 ? _lineItemsByServiceId_get : [],
            lineItem
        ]);
    }, new Map());
// WHY: optionality is a property of the service's participation (committed-scope.go), constant for the schedule.
const serviceIdsOf = (lineItems)=>new Set(lineItems.map((lineItem)=>lineItem.serviceId()));
export const laneDotKeyOf = (invoice, serviceId)=>"".concat(invoice.key(), "-").concat(serviceId);
const dotTaxOf = (invoice, serviceLineItems)=>{
    const invoiceTax = invoice.tax();
    const lineTax = serviceLineItems.length === 1 ? serviceLineItems[0].tax() : UNTAXED;
    return invoiceTax.kind === 'taxed' && lineTax.kind === 'taxed' ? {
        kind: 'taxed',
        label: invoiceTax.label,
        formattedTaxAmount: currencyUtils.formatCents(lineTax.taxedAmount.taxAmount)
    } : UNTAXED;
};
const dotStageOf = (lineItems)=>{
    const stages = compact(lineItems.map((lineItem)=>lineItem.stage()));
    return stages.length === 0 ? null : {
        numbers: stages.map((stage)=>stage.number).sort((left, right)=>left - right),
        count: stages[0].count
    };
};
const laneDotsByServiceId = (invoices, horizon, committedServiceIds)=>invoices.filter((invoice)=>horizon.containsDate(invoice.date())).reduce((dotsByServiceId, invoice)=>{
        const offsetPercent = horizon.offsetPercentOf(invoice.date());
        const formattedDate = invoice.formattedDateWithYear();
        lineItemsByServiceIdOf(invoice).forEach((serviceLineItems, serviceId)=>{
            var _dotsByServiceId_get;
            const dots = (_dotsByServiceId_get = dotsByServiceId.get(serviceId)) !== null && _dotsByServiceId_get !== void 0 ? _dotsByServiceId_get : [];
            dots.push({
                key: laneDotKeyOf(invoice, serviceId),
                offsetPercent,
                formattedAmount: currencyUtils.formatCents(sumAmounts(serviceLineItems)),
                formattedDate,
                isFinal: serviceLineItems.some((lineItem)=>lineItem.isFinalCharge()),
                isOneTime: serviceLineItems.every((lineItem)=>lineItem.billingType() === 'oneTime'),
                isProrated: serviceLineItems.some((lineItem)=>lineItem.isProrated()),
                isOptional: !committedServiceIds.has(serviceId),
                isCatchUp: serviceLineItems.some((lineItem)=>lineItem.isCatchUp()),
                stage: dotStageOf(serviceLineItems),
                tax: dotTaxOf(invoice, serviceLineItems)
            });
            dotsByServiceId.set(serviceId, dots);
        });
        return dotsByServiceId;
    }, new Map());
const lineItemsOf = (invoices)=>invoices.flatMap((invoice)=>invoice.lineItems());
const committedLineItemsOf = (invoices)=>committedAmong(lineItemsOf(invoices));
const forwardLookingLineItemsOf = (invoices)=>committedLineItemsOf(invoices).filter((lineItem)=>!lineItem.isCatchUp());
const oneTimeLineItemsOf = (invoices)=>committedLineItemsOf(invoices).filter((lineItem)=>lineItem.billingType() === 'oneTime');
const rollUpByService = (lineItems)=>{
    const rollupsByServiceId = lineItems.reduce((rollups, lineItem)=>{
        var _ref, _ref1;
        const openRollup = rollups.get(lineItem.serviceId());
        const total = ((_ref = openRollup === null || openRollup === void 0 ? void 0 : openRollup.total) !== null && _ref !== void 0 ? _ref : 0) + lineItem.amount();
        return rollups.set(lineItem.serviceId(), {
            serviceId: lineItem.serviceId(),
            name: (_ref1 = openRollup === null || openRollup === void 0 ? void 0 : openRollup.name) !== null && _ref1 !== void 0 ? _ref1 : lineItem.name(),
            total
        });
    }, new Map());
    return [
        ...rollupsByServiceId.values()
    ];
};
const optionalOnlyServicesOf = (invoices)=>{
    const nameByServiceId = optionalAmong(lineItemsOf(invoices)).reduce((names, lineItem)=>names.has(lineItem.serviceId()) ? names : names.set(lineItem.serviceId(), lineItem.name()), new Map());
    return [
        ...nameByServiceId.entries()
    ].map((param)=>{
        let [serviceId, name] = param;
        return {
            serviceId,
            name
        };
    }).sort((left, right)=>left.name.localeCompare(right.name));
};
const billingOrderedLineItemsOf = (invoices)=>committedLineItemsOf([
        ...invoices
    ].sort(byDate));
const billingOrderedServiceIdsOf = (invoices)=>[
        ...new Set(billingOrderedLineItemsOf(invoices).map((lineItem)=>lineItem.serviceId()))
    ];
const amountByServiceIdOf = (invoices, billingOrderedServiceIds)=>{
    const amounts = committedLineItemsOf(invoices).reduce((totals, lineItem)=>{
        var _totals_lineItem_serviceId;
        totals[lineItem.serviceId()] = ((_totals_lineItem_serviceId = totals[lineItem.serviceId()]) !== null && _totals_lineItem_serviceId !== void 0 ? _totals_lineItem_serviceId : 0) + lineItem.amount();
        return totals;
    }, {});
    return Object.fromEntries(billingOrderedServiceIds.filter((serviceId)=>amounts[serviceId] != null).map((serviceId)=>[
            serviceId,
            amounts[serviceId]
        ]));
};
const billedInvoicesOf = (invoices)=>invoices.map((invoice)=>({
            key: invoice.key(),
            formattedDate: invoice.formattedDate(),
            formattedTotal: invoice.formattedTotal(),
            formattedOptionalAmount: invoice.hasOptionalCharges() ? invoice.formattedOptionalAmount() : null,
            isCatchUp: invoice.isCatchUp(),
            lines: committedAmong(invoice.lineItems()).map((lineItem, position)=>({
                    key: "".concat(invoice.key(), "-").concat(position),
                    serviceId: lineItem.serviceId(),
                    name: lineItem.name(),
                    formattedAmount: lineItem.formattedAmount()
                })),
            optionalLines: optionalAmong(invoice.lineItems()).map((lineItem, position)=>({
                    key: "".concat(invoice.key(), "-optional-").concat(position),
                    serviceId: lineItem.serviceId(),
                    name: lineItem.name(),
                    formattedAmount: lineItem.formattedAmount()
                }))
        }));
const wireMonthKeyOf = (param)=>{
    let { year, month } = param;
    return "".concat(year, "-").concat(String(month).padStart(2, '0'));
};
const monthTaxOf = (invoices, monthTotals)=>{
    const invoiceTax = invoices.map((invoice)=>invoice.tax()).find((tax)=>tax.kind === 'taxed');
    return (invoiceTax === null || invoiceTax === void 0 ? void 0 : invoiceTax.kind) === 'taxed' && monthTotals != null ? {
        kind: 'taxed',
        label: invoiceTax.label,
        subtotal: monthTotals.totals.subtotal,
        tax: monthTotals.totals.tax
    } : UNTAXED;
};
const dropSpilledPartMonth = (months, anchor)=>{
    const spillsIntoAPartMonth = anchor.day !== 1;
    return spillsIntoAPartMonth ? months.slice(0, -1) : months;
};
const byDate = (left, right)=>dayOrderOf(left.date()) - dayOrderOf(right.date());
const groupByMonth = (invoices)=>{
    const invoicesByMonthOrder = new Map();
    invoices.forEach((invoice)=>{
        var _invoicesByMonthOrder_get;
        const monthOrder = monthOrderOf(calendarMonthOf(invoice.date()));
        invoicesByMonthOrder.set(monthOrder, [
            ...(_invoicesByMonthOrder_get = invoicesByMonthOrder.get(monthOrder)) !== null && _invoicesByMonthOrder_get !== void 0 ? _invoicesByMonthOrder_get : [],
            invoice
        ]);
    });
    return [
        ...invoicesByMonthOrder.entries()
    ].sort((param, param1)=>{
        let [earlierMonth] = param, [laterMonth] = param1;
        return earlierMonth - laterMonth;
    }).map((param)=>{
        let [, monthInvoices] = param;
        const orderedInvoices = [
            ...monthInvoices
        ].sort(byDate);
        return new InvoiceMonthGroupModel(calendarMonthOf(orderedInvoices[0].date()), orderedInvoices);
    });
};
export class BillingTimelineModel {
    static fromDTO(dto) {
        return new BillingTimelineModel(toCalendarDate(dto.anchorDate), toList(dto.invoices).map(ScheduledInvoiceModel.fromDTO), toFlag(dto.hasManuallyBilledServices), dto.annualRecurringValue == null ? null : currencyUtils.dollarsToCents(new Decimal(dto.annualRecurringValue)), toMonthTotals(dto.months));
    }
    horizon() {
        return this.billingHorizon;
    }
    anchorDate() {
        return this.simulatedFrom;
    }
    invoices() {
        return this.scheduledInvoices;
    }
    hasInvoices() {
        return this.scheduledInvoices.length > 0;
    }
    hasManuallyBilledServices() {
        return this.manuallyBilledServices;
    }
    hasCatchUpInvoices() {
        return this.scheduledInvoices.some((invoice)=>invoice.isCatchUp());
    }
    hasAppliedTax() {
        return this.scheduledInvoices.some((invoice)=>invoice.tax().kind === 'taxed');
    }
    invoicesByMonth() {
        return groupByMonth(this.scheduledInvoices);
    }
    firstInvoice() {
        return this.scheduledInvoices.find((invoice)=>invoice.carriesCommittedCharges());
    }
    formattedFirstInvoiceAmount() {
        var _ref;
        var _this_firstInvoice;
        return currencyUtils.formatCents((_ref = (_this_firstInvoice = this.firstInvoice()) === null || _this_firstInvoice === void 0 ? void 0 : _this_firstInvoice.totalAmount()) !== null && _ref !== void 0 ? _ref : 0);
    }
    monthlyTotals() {
        const invoicesByMonthKey = this.invoicesByMonth().reduce((invoices, group)=>{
            var _invoices_get;
            return invoices.set(group.key(), [
                ...(_invoices_get = invoices.get(group.key())) !== null && _invoices_get !== void 0 ? _invoices_get : [],
                ...group.invoices()
            ]);
        }, new Map());
        const billingOrderedServiceIds = billingOrderedServiceIdsOf(this.scheduledInvoices);
        const plottedMonths = this.billingHorizon.billingMonths().map((month)=>{
            var _invoicesByMonthKey_get, _ref;
            var _this_monthTotalsByKey;
            const invoices = (_invoicesByMonthKey_get = invoicesByMonthKey.get(monthKeyOf(month))) !== null && _invoicesByMonthKey_get !== void 0 ? _invoicesByMonthKey_get : [];
            const monthTotals = (_this_monthTotalsByKey = this.monthTotalsByKey) === null || _this_monthTotalsByKey === void 0 ? void 0 : _this_monthTotalsByKey.get(wireMonthKeyOf(month));
            const total = (_ref = monthTotals === null || monthTotals === void 0 ? void 0 : monthTotals.totalAmount) !== null && _ref !== void 0 ? _ref : invoices.reduce((monthTotal, invoice)=>monthTotal + invoice.totalAmount(), 0);
            const optionalTotal = invoices.reduce((monthUpside, invoice)=>monthUpside + invoice.optionalAmount(), 0);
            return {
                key: monthKeyOf(month),
                heading: monthHeadingOf(month),
                tickLabel: monthTickLabelOf(month),
                total,
                optionalTotal,
                formattedOptionalTotal: optionalTotal > 0 ? currencyUtils.formatCents(optionalTotal) : null,
                formattedTotal: currencyUtils.formatCents(total),
                tax: monthTaxOf(invoices, monthTotals),
                invoiceCount: invoices.length,
                billedInvoices: billedInvoicesOf(invoices),
                amountByServiceId: amountByServiceIdOf(invoices, billingOrderedServiceIds)
            };
        });
        return dropSpilledPartMonth(plottedMonths, this.simulatedFrom);
    }
    annualRecurringValue() {
        return sumAmounts(forwardLookingLineItemsOf(this.scheduledInvoices).filter((lineItem)=>lineItem.billingType() === 'recurring'));
    }
    formattedAnnualRecurringValue() {
        return currencyUtils.formatCents(this.annualRecurringValue());
    }
    termsBasedAnnualRecurringValue() {
        return this.termsBasedRecurringValueInCents;
    }
    oneTimeContractValue() {
        return sumAmounts(oneTimeLineItemsOf(this.scheduledInvoices));
    }
    hasOneTimeItems() {
        return oneTimeLineItemsOf(this.scheduledInvoices).length > 0;
    }
    formattedOneTimeContractValue() {
        return currencyUtils.formatCents(this.oneTimeContractValue());
    }
    totalContractValue() {
        return sumAmounts(committedLineItemsOf(this.scheduledInvoices));
    }
    formattedTotalContractValue() {
        return currencyUtils.formatCents(this.totalContractValue());
    }
    amountsByService() {
        return rollUpByService(billingOrderedLineItemsOf(this.scheduledInvoices));
    }
    services() {
        return [
            ...rollUpByService(billingOrderedLineItemsOf(this.scheduledInvoices)).map((param)=>{
                let { serviceId, name } = param;
                return {
                    serviceId,
                    name
                };
            }),
            ...optionalOnlyServicesOf(this.scheduledInvoices)
        ];
    }
    serviceLanes() {
        const dotsByServiceId = laneDotsByServiceId(this.scheduledInvoices, this.billingHorizon, serviceIdsOf(committedLineItemsOf(this.scheduledInvoices)));
        return this.services().map((service)=>{
            var _dotsByServiceId_get;
            return {
                serviceId: service.serviceId,
                name: service.name,
                dots: (_dotsByServiceId_get = dotsByServiceId.get(service.serviceId)) !== null && _dotsByServiceId_get !== void 0 ? _dotsByServiceId_get : []
            };
        });
    }
    horizonTicks() {
        return this.billingHorizon.tickMonths().map((opening)=>({
                key: monthKeyOf(opening),
                label: monthTickLabelOf(opening),
                offsetPercent: this.billingHorizon.offsetPercentOf(_object_spread_props(_object_spread({}, opening), {
                    day: 1
                }))
            }));
    }
    invoiceDots() {
        return [
            ...this.scheduledInvoices
        ].sort(byDate).map((invoice, index)=>({
                key: "".concat(invoice.key(), "-").concat(index),
                invoice,
                offsetPercent: this.billingHorizon.offsetPercentOf(invoice.date()),
                formattedDate: invoice.formattedDateWithYear(),
                formattedTotal: invoice.formattedTotal()
            }));
    }
    anchorOffsetPercent() {
        return this.billingHorizon.anchorOffsetPercent();
    }
    offsetPercentOf(date) {
        return this.billingHorizon.containsDate(date) ? this.billingHorizon.offsetPercentOf(date) : null;
    }
    constructor(simulatedFrom, scheduledInvoices, manuallyBilledServices, termsBasedRecurringValueInCents, monthTotalsByKey){
        _define_property(this, "simulatedFrom", void 0);
        _define_property(this, "scheduledInvoices", void 0);
        _define_property(this, "manuallyBilledServices", void 0);
        _define_property(this, "termsBasedRecurringValueInCents", void 0);
        _define_property(this, "monthTotalsByKey", void 0);
        _define_property(this, "billingHorizon", void 0);
        this.simulatedFrom = simulatedFrom;
        this.scheduledInvoices = scheduledInvoices;
        this.manuallyBilledServices = manuallyBilledServices;
        this.termsBasedRecurringValueInCents = termsBasedRecurringValueInCents;
        this.monthTotalsByKey = monthTotalsByKey;
        this.billingHorizon = new BillingTimelineHorizon(windowStartOf(simulatedFrom, scheduledInvoices), simulatedFrom);
    }
}
